/**
 * Purpose:
 *   Reusable icon component that displays SVG assets.
 *   Supports two rendering modes: mask (single color) and image (multi-color).
 *
 * Notes:
 *   - Default mode uses CSS mask for color control via Tailwind classes
 *   - preserveColors mode renders SVG as image, preserving original colors
 *   - Use preserveColors for multi-color icons (e.g., navigation icons)
 *
 * @example Single color icon (mask mode - default)
 * <Icon src="/icons/eye.svg" size={20} className="text-neutral-500" />
 *
 * @example Multi-color icon (preserveColors mode)
 * <Icon src="/icons/navigation/dashboard.svg" size={20} preserveColors />
 */

'use client';

import { cx } from '@/util/functions/cx';
import React from 'react';
import { iconStyles } from './Icon.styles';

export interface IIconProps {
  /** URL path to the SVG asset (e.g., "/icons/eye.svg") */
  src: string;
  /** Optional size in pixels (applied as width and height) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** When true, renders SVG as image preserving original colors */
  preserveColors?: boolean;
  /** Optional aria-label for accessibility */
  'aria-label'?: string;
  /** Optional aria-hidden for decorative icons */
  'aria-hidden'?: boolean;
}

export const Icon: React.FC<IIconProps> = ({
  src,
  size,
  className,
  preserveColors = false,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = true,
}) => {
  const sizeStyle = size
    ? { width: `${size}px`, height: `${size}px` }
    : undefined;

  // Multi-color mode: render as image to preserve original SVG colors
  if (preserveColors) {
    return (
      <span
        className={cx(iconStyles.container, className)}
        style={sizeStyle}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={ariaLabel || ''}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </span>
    );
  }

  // Single-color mode: use CSS mask for color control via Tailwind classes
  return (
    <span
      className={cx(iconStyles.container, className)}
      style={sizeStyle}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    >
      <span
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          backgroundColor: 'currentColor',
          WebkitMaskImage: `url(${src})`,
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: `url(${src})`,
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
        }}
      />
    </span>
  );
};

Icon.displayName = 'Icon';
